import { Writeable } from '@/utils/Writeable'
import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useEffect,
  useRef,
  useState
} from 'react'
import { FileObject } from 'react-mui-dropzone'

import { fiscalRecordTypeCatalog } from '../ComplianceFiscal/helpers/fiscalRecordTypeCatalog'
import { emptyComplianceContext } from './helpers/emptyComplianceContext'

export const recordTypeCatalog = {
  criminal: ['Compliance Criminal'],
  environmental: ['Environmental Debts', 'Environmental Embargoes'],
  fiscal: Object.keys(fiscalRecordTypeCatalog),
  labor: ['CNDT', 'FGTS']
} as const

export const documentStatusCatalog = {
  criminal: ['POSITIVE', 'NEGATIVE', 'UNDEFINED'],
  environmental: ['POSITIVE', 'NEGATIVE', 'POSITIVE_WITH_NEGATIVE_EFFECTS', 'UNDEFINED'],
  fiscal: ['POSITIVE', 'NEGATIVE', 'POSITIVE_WITH_NEGATIVE_EFFECTS', 'UNDEFINED'],
  labor: ['POSITIVE', 'NEGATIVE', 'UNDEFINED']
} as const

export type TPersonProfile = {
  canonicalProfileId?: string
  govRegCode?: string
  govRegType?: 'LEGAL' | 'NATURAL'
  id?: string
  name?: string
}

/**
 * A record candidate is a generic compliance record created by the user, but not yet posted to the back-end.
 */
export interface IRecordCandidate {
  index?: number
  recordType: Writeable<(typeof recordTypeCatalog)[keyof typeof recordTypeCatalog][number]>
  documentStatus: Writeable<
    (typeof documentStatusCatalog)[keyof typeof documentStatusCatalog][number]
  >
  documentExpirationDate: Date
  file: FileObject
}

export interface IDeleteRecord {
  id: string
  endpoint?: Writeable<(typeof recordTypeCatalog)[keyof typeof recordTypeCatalog][number]>
}

export interface IComplianceContext {
  personProfile: TPersonProfile
  setPersonProfile: Dispatch<SetStateAction<TPersonProfile>>
  recordCandidates: IRecordCandidate[]
  addRecordCandidate: (newRecordCandidate: IRecordCandidate) => void
  removeRecordCandidate: (recordCandidateIndex: number) => void
  deleteRecords: IDeleteRecord[]
  addDeleteRecord: (record: IDeleteRecord) => void
  hasPendingModifications: boolean
  resetContext: () => void
  numOfResets: number
}

export const ComplianceContext = createContext<IComplianceContext>(emptyComplianceContext)

export const ComplianceProvider = ({ children }: { children: ReactNode }) => {
  const [recordCandidates, setRecordCandidates] = useState<IRecordCandidate[]>([])
  const [deleteRecords, setDeleteRecords] = useState<IDeleteRecord[]>([])
  const [personProfile, setPersonProfile] = useState<TPersonProfile>(
    emptyComplianceContext.personProfile
  )
  const [hasPendingModifications, setHasPendingModifications] = useState(false)
  const [numOfResets, setResetCounter] = useState(0)
  const index = useRef(0)

  const resetContext = () => {
    setRecordCandidates([])
    setDeleteRecords([])
    setHasPendingModifications(false)
    setResetCounter(prevResetCounter => prevResetCounter + 1)
    index.current = 0
  }

  const addRecordCandidate = (newRecordCandidate: IRecordCandidate) => {
    setRecordCandidates(prevRecordCandidates => [
      ...prevRecordCandidates,
      { ...newRecordCandidate, index: index.current }
    ])
    index.current += 1
  }

  const removeRecordCandidate = (recordCandidateIndex: number) => {
    setRecordCandidates(prevRecordCandidates =>
      prevRecordCandidates.filter(({ index: id }) => id !== recordCandidateIndex)
    )
  }

  const addDeleteRecord = (record: IDeleteRecord) => {
    setDeleteRecords(prevDeleteRecords => [...prevDeleteRecords, record])
  }

  useEffect(() => {
    setHasPendingModifications(recordCandidates.length > 0 || deleteRecords.length > 0)
  }, [recordCandidates, deleteRecords])

  return (
    <ComplianceContext.Provider
      value={{
        personProfile,
        setPersonProfile,
        recordCandidates,
        addRecordCandidate,
        removeRecordCandidate,
        deleteRecords,
        addDeleteRecord,
        hasPendingModifications,
        resetContext,
        numOfResets
      }}>
      {children}
    </ComplianceContext.Provider>
  )
}
